import sessionApi, { listResults, paginatedResult, PaginatedResult } from './apiClient';
import { authService } from './authService';
import { ChainOfCustodyEvent, DepartmentSummary, Firearm, ReturnRecord } from '../types/domain';

export interface OfficerPayload {
  first_name: string;
  middle_name: string;
  last_name: string;
  badge_number: string;
  rank: string;
  unit_assignment: string;
  department?: number | '';
  ltopf_number: string;
  ltopf_expiration_date: string;
  employment_status: string;
}

export interface FirearmPayload {
  serial_number: string;
  article: string;
  description: string;
  model: string;
  caliber: string;
  servicability: string;
  acquisition_date: string;
  status: string;
  is_supported_for_issuance?: boolean;
}

export interface FirearmIncidentPayload {
  firearm: number;
  officer?: number;
  incident_type: string;
  incident_date: string;
  location: string;
  description: string;
}

export interface FirearmFilters {
  search?: string;
  status?: string;
  servicability?: string;
  assigned_to?: number;
  serial_number?: string;
  brand_model?: string;
  article?: string;
  deployment_unit?: string;
  department?: number;
  admin_department?: number;
  issuance_eligible?: boolean;
  page?: number;
  page_size?: number;
  ordering?: string;
}

type AdminDepartmentFilter = { admin_department?: number };

export const inventoryService = {
  getOfficers: async (filters: AdminDepartmentFilter = {}) => {
    const response = await sessionApi.get('/officers/', { params: filters });
    return listResults<any>(response.data);
  },

  getEligibleIssuanceOfficers: async (filters: AdminDepartmentFilter = {}) => {
    const response = await sessionApi.get('/officers/', { params: { ...filters, issuance_eligible: true } });
    return listResults<any>(response.data);
  },

  createOfficer: async (payload: OfficerPayload) => {
    const response = await sessionApi.post('/officers/', payload);
    return response.data;
  },

  updateOfficer: async (id: number, payload: Partial<OfficerPayload>) => {
    const response = await sessionApi.patch(`/officers/${id}/`, payload);
    return response.data;
  },

  getDepartments: async (): Promise<DepartmentSummary[]> => {
    try {
      const response = await sessionApi.get('/departments/accessible/');
      return listResults<DepartmentSummary>(response.data);
    } catch (error) {
      const profileResponse = await authService.getProfile();
      return (profileResponse.user?.departments || []).filter((department) => department.is_active !== false);
    }
  },

  deleteOfficer: async (id: number) => {
    const response = await sessionApi.delete(`/officers/${id}/`);
    return response.data;
  },

  setOfficerActivation: async (id: number, active: boolean) => {
    const response = await sessionApi.post(`/officers/${id}/activation/`, { active });
    return response.data;
  },

  getFirearms: async (filters: FirearmFilters = {}): Promise<Firearm[]> => {
    const response = await sessionApi.get('/firearms/', { params: filters });
    return listResults<Firearm>(response.data);
  },

  getFirearmsPage: async (filters: FirearmFilters = {}): Promise<PaginatedResult<Firearm>> => {
    const response = await sessionApi.get('/firearms/', { params: filters });
    return paginatedResult<Firearm>(response.data);
  },

  getEligibleIssuanceFirearms: async (filters: AdminDepartmentFilter = {}): Promise<Firearm[]> => {
    const response = await sessionApi.get('/firearms/', { params: { ...filters, issuance_eligible: true } });
    return listResults<Firearm>(response.data);
  },

  createFirearm: async (payload: FirearmPayload | FormData) => {
    const formData = payload instanceof FormData ? payload : (() => {
      const data = new FormData();
      Object.entries(payload).forEach(([key, entry]) => {
        if (entry !== undefined && entry !== null && entry !== '') {
          data.append(key, String(entry));
        }
      });
      return data;
    })();
    const response = await sessionApi.post('/firearms/', formData, {
      headers: { 'Content-Type': undefined },
    });
    return response.data;
  },

  updateFirearm: async (id: number, payload: Partial<FirearmPayload>) => {
    const response = await sessionApi.patch(`/firearms/${id}/`, payload);
    return response.data;
  },

  getCustodyLogs: async (
    filters: { firearm?: number; officer?: number; action_type?: string; department_id?: number | string; require_department?: boolean; admin_department?: number } = {}
  ): Promise<ChainOfCustodyEvent[]> => {
    const response = await sessionApi.get('/chain-of-custody/', { params: filters });
    return listResults<ChainOfCustodyEvent>(response.data);
  },

  getReturnRecords: async (
    filters: { firearm?: number; officer?: number; action_type?: string; admin_department?: number } = {}
  ): Promise<ReturnRecord[]> => {
    const response = await sessionApi.get('/firearm-return-records/', { params: filters });
    return listResults<ReturnRecord>(response.data);
  },

  getFirearmIncidents: async (
    filters: { firearm?: number; officer?: number; incident_type?: string; status?: string; is_active?: boolean; admin_department?: number } = {}
  ) => {
    const response = await sessionApi.get('/firearm-incidents/', { params: filters });
    return listResults<any>(response.data);
  },

  createFirearmIncident: async (payload: FirearmIncidentPayload) => {
    const response = await sessionApi.post('/firearm-incidents/', payload);
    return response.data;
  },

  updateFirearmIncidentStatus: async (id: number, payload: { status: string; remarks?: string }) => {
    const response = await sessionApi.post(`/firearm-incidents/${id}/status/`, payload);
    return response.data;
  },

  acknowledgeFirearmIncident: async (id: number) => {
    const response = await sessionApi.post(`/firearm-incidents/${id}/status/`, {
      status: 'under_investigation',
      remarks: 'Acknowledged by Logistics.',
    });
    return response.data;
  },

  closeFirearmIncident: async (id: number, remarks: string) => {
    const response = await sessionApi.post(`/firearm-incidents/${id}/status/`, {
      status: 'closed',
      remarks,
    });
    return response.data;
  },

  reviewFirearmIncident: async (id: number, payload: { status: string; review_notes?: string }) => {
    const response = await sessionApi.post(`/firearm-incidents/${id}/review/`, payload);
    return response.data;
  },
};
